import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Users,
  Image,
  User,
  Calendar,
  Settings,
  LogOut,
  Menu,
  X,
  LayoutDashboard,
} from 'lucide-react';
import { useAppStore } from '../store/useAppStore';

const Layout = ({ children }) => {
  const location = useLocation();
  const { setIsLoggingOut, setIsCheckingAuth } = useAppStore();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const handleLogout = () => {
    setIsLoggingOut(true);
    setIsCheckingAuth(false);
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Social Accounts', href: '/socials', icon: Users },
    { name: 'Content Gallery', href: '/gallery', icon: Image },
    { name: 'Calendar', href: '/schedule', icon: Calendar },
    { name: 'Profile', href: '/profile', icon: User },
    { name: 'Settings', href: '/settings', icon: Settings },
    { name: 'Logout', href: '#', icon: LogOut, onClick: handleLogout },
  ];

  const isCurrentPath = (path) => {
    return location.pathname === path;
  };

  return (
    <div className='min-h-screen bg-gray-50'>
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div
          className='fixed inset-0 bg-gray-600 bg-opacity-75'
          onClick={() => setSidebarOpen(false)}
        />
        <div className='fixed inset-y-0 left-0 flex w-56 flex-col bg-white'>
          <div className='flex h-16 items-center justify-between px-4'>
            <h1 className='text-xl font-bold text-gray-900'>MarketChef</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className='text-gray-400 hover:text-gray-600'
            >
              <X className='h-6 w-6' />
            </button>
          </div>
          <nav className='flex-1 space-y-1 px-2 py-4'>
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isCurrentPath(item.href)
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={(e) => {
                    setSidebarOpen(false);
                    if (item.onClick) {
                      e.preventDefault();
                      item.onClick();
                    }
                  }}
                >
                  <Icon className='mr-3 h-5 w-5 flex-shrink-0' />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className='hidden lg:fixed lg:inset-y-0 lg:flex lg:w-56 lg:flex-col'>
        <div className='flex flex-col flex-grow bg-white border-r border-gray-200'>
          <div className='flex h-16 items-center px-4'>
            <h1 className='text-xl font-bold text-gray-900'>MarketChef</h1>
          </div>
          <nav className='flex-1 space-y-1 px-2 py-4'>
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isCurrentPath(item.href)
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={(e) => {
                    if (item.onClick) {
                      e.preventDefault();
                      item.onClick();
                    }
                  }}
                >
                  <Icon className='mr-3 h-5 w-5 flex-shrink-0' />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className='lg:pl-56'>
        {/* Top bar */}
        <div className='sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8'>
          <button
            type='button'
            className='-m-2.5 p-2.5 text-gray-700 lg:hidden'
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className='h-6 w-6' />
          </button>

          <div className='flex flex-1 gap-x-4 self-stretch lg:gap-x-6'>
            <div className='flex flex-1'></div>
            <div className='flex items-center gap-x-4 lg:gap-x-6'>
              <div className='hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200' />
              <div className='text-sm text-gray-500'>{format(new Date(), 'PPP')}</div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className='py-4 bg-gradient-to-br from-gray-50 to-blue-50'>
          <div className='mx-auto max-w-7xl px-4'>{children}</div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
